import {
  Button,
  Card,
  Dialog,
  DialogPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
} from "@tremor/react";
import { Building2, Plus, Trash2, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  addChildMember,
  createChildWorkspace,
  deleteChildWorkspace,
  fetchChildMembers,
  removeChildMember,
} from "../api/workspaces.js";
import { useChildWorkspaces, useMembers } from "../queries/workspaces.js";
import { useQueryClient } from "@tanstack/react-query";
import Badge from "../components/Badge.jsx";
import { useAuthStore } from "../store/authStore.js";
import { usePermission } from "../lib/usePermission.js";

import { useTranslation } from "react-i18next";

const MEMBER_ROLES = ["owner", "admin", "member", "viewer"];

// ---------------------------------------------------------------------------
// Create child workspace modal
// ---------------------------------------------------------------------------

function CreateWorkspaceModal({ onClose, onCreated }) {
  const { t } = useTranslation("workspaces");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState("standard");
  const [saving, setSaving] = useState(false);

  function handleNameChange(v) {
    setName(v);
    if (!slug || slug === toSlug(name)) {
      setSlug(toSlug(v));
    }
  }

  function toSlug(s) {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const workspace = await createChildWorkspace({
        name: name.trim(),
        slug: slug.trim(),
        plan,
      });
      toast.success(t("modal.success"));
      onCreated(workspace);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || t("modal.error");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="w-full max-w-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{t("modal.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.name")}</label>
            <TextInput
              required
              value={name}
              onValueChange={handleNameChange}
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">{t("modal.slug")}</label>
            <TextInput
              required
              value={slug}
              onValueChange={setSlug}
              placeholder="acme-corp"
            />
            <p className="mt-1 text-xs text-gray-400">
              {t("modal.slug_help")}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Workspace Type (Plan)</label>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="plan"
                  value="standard"
                  checked={plan === "standard"}
                  onChange={() => setPlan("standard")}
                  className="h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                />
                <span>Standard (Sites & platform checks)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="plan"
                  value="professional"
                  checked={plan === "professional"}
                  onChange={() => setPlan("professional")}
                  className="h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                />
                <span>Professional (On-Call, RUM, Servers, Groups)</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={onClose}>
              {t("common:common.cancel")}
            </Button>
            <Button type="submit" disabled={saving || !name || !slug}>
              {saving ? t("modal.creating") : t("modal.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Manage members drawer for a child workspace
// ---------------------------------------------------------------------------

function ManageMembersDrawer({ workspace, parentMembers, onClose }) {
  const { t } = useTranslation("workspaces");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingUserId, setAddingUserId] = useState("");
  const [addingRole, setAddingRole] = useState("member");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchChildMembers(workspace.id)
      .then(setMembers)
      .catch(() => toast.error(t("drawer.load_error")))
      .finally(() => setLoading(false));
  }, [workspace.id, t]);

  const memberIds = new Set(members.map((m) => String(m.user_id)));
  const eligible = parentMembers.filter((m) => !memberIds.has(String(m.user_id)));

  async function handleAdd(e) {
    e.preventDefault();
    if (!addingUserId) return;
    setSaving(true);
    try {
      const member = await addChildMember(workspace.id, { user_id: addingUserId, role: addingRole });
      setMembers((prev) => [...prev, member]);
      setAddingUserId("");
      setAddingRole("member");
      toast.success(t("drawer.success_add"));
    } catch (err) {
      const msg = err?.response?.data?.error || t("drawer.error");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(userId) {
    try {
      await removeChildMember(workspace.id, userId);
      setMembers((prev) => prev.filter((m) => String(m.user_id) !== String(userId)));
      toast.success(t("drawer.success_remove"));
    } catch (err) {
      const msg = err?.response?.data?.error || t("drawer.error");
      toast.error(msg);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-md flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t("drawer.title")}</h2>
            <p className="mt-0.5 text-xs text-gray-400">{workspace.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Add member */}
          {eligible.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-700">
                {t("drawer.add_title")}
              </h3>
              <form onSubmit={handleAdd} className="flex gap-2">
                <select
                  value={addingUserId}
                  onChange={(e) => setAddingUserId(e.target.value)}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                >
                  <option value="">{t("drawer.select_user")}</option>
                  {eligible.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.email}
                    </option>
                  ))}
                </select>
                <select
                  value={addingRole}
                  onChange={(e) => setAddingRole(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                >
                  {MEMBER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <Button type="submit" disabled={saving || !addingUserId}>
                  {t("drawer.add_button")}
                </Button>
              </form>
            </div>
          )}

          {/* Current members */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">{t("drawer.current_title")}</h3>
            {loading ? (
              <p className="text-sm text-gray-400">{t("loading")}</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-400">{t("drawer.no_members")}</p>
            ) : (
              <div className="space-y-1">
                {members.map((m) => (
                  <div
                    key={m.user_id}
                    className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-gray-50"
                  >
                    <div>
                      <p className="text-sm text-gray-800">{m.email}</p>
                      <p className="text-xs text-gray-400">{m.role}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(m.user_id)}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Workspaces page
// ---------------------------------------------------------------------------

export default function Workspaces() {
  const { t } = useTranslation(["workspaces", "common"]);
  const navigate = useNavigate();
  const setWorkspace = useAuthStore((s) => s.setWorkspace);
  const activeWorkspace = useAuthStore((s) => s.workspace);
  const [showCreate, setShowCreate] = useState(false);
  const [managingWorkspace, setManagingWorkspace] = useState(null);
  // Workspace management only makes sense on a root (agency) workspace — child
  // workspaces can't own sub-workspaces. The sidebar entry stays visible, but
  // the screen itself is gated.
  const isRoot = activeWorkspace?.parent_id == null;
  const canCreate = usePermission("workspaces.create");
  const canManage = usePermission("workspaces.manage");
  const canDelete = usePermission("workspaces.delete");

  const qc = useQueryClient();
  const childWorkspacesQuery = useChildWorkspaces();
  const parentMembersQuery = useMembers();
  const workspaces = childWorkspacesQuery.data ?? [];
  const parentMembers = parentMembersQuery.data ?? [];
  const loading = childWorkspacesQuery.isPending || parentMembersQuery.isPending;

  useEffect(() => {
    if (childWorkspacesQuery.isError) toast.error(t("drawer.load_error"));
  }, [childWorkspacesQuery.isError, t]);

  function handleSwitch(workspace) {
    setWorkspace(workspace);
    navigate("/dashboard", { replace: true });
  }

  async function handleDelete(workspace) {
    if (!confirm(t("table.delete_confirm", { name: workspace.name }))) return;
    try {
      await deleteChildWorkspace(workspace.id);
      qc.invalidateQueries({ queryKey: ["workspaces", "children"] });
      qc.invalidateQueries({ queryKey: ["billing"] });
      toast.success(t("table.success_delete"));
    } catch (err) {
      const msg = err?.response?.data?.error || t("table.error_delete");
      toast.error(msg);
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t("subtitle")}
          </p>
        </div>
        {canCreate && isRoot && (
          <Button icon={Plus} onClick={() => setShowCreate(true)}>
            {t("new_workspace")}
          </Button>
        )}
      </div>

      <Card className="p-0">
        {!isRoot ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500">{t("root_only")}</p>
        ) : loading ? (
          <p className="px-5 py-8 text-center text-sm text-gray-400">{t("loading")}</p>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t("table.name")}</TableHeaderCell>
                <TableHeaderCell>{t("table.slug")}</TableHeaderCell>
                <TableHeaderCell>{t("table.plan")}</TableHeaderCell>
                <TableHeaderCell>{t("table.monitors")}</TableHeaderCell>
                <TableHeaderCell>{t("table.active_incidents")}</TableHeaderCell>
                <TableHeaderCell>{t("table.members")}</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Current (active) workspace — always shown first, tagged */}
              {activeWorkspace && (
                <TableRow key={activeWorkspace.id} className="bg-teal-50/40">
                  <TableCell className="font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {activeWorkspace.name}
                      <Badge color="teal">{t("table.current")}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-gray-500">{activeWorkspace.slug}</span>
                  </TableCell>
                  <TableCell>
                    <Badge color="gray">{activeWorkspace.plan}</Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">—</TableCell>
                  <TableCell className="text-gray-400">—</TableCell>
                  <TableCell className="text-gray-400">—</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
              {workspaces.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <Building2 className="mb-3 h-10 w-10 text-gray-300" />
                      <p className="text-sm font-medium text-gray-600">{t("empty.title")}</p>
                      <p className="mt-1 text-xs text-gray-400">{t("empty.description")}</p>
                      {canCreate && (
                        <Button className="mt-4" icon={Plus} onClick={() => setShowCreate(true)}>
                          {t("new_workspace")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {workspaces
                .filter((ws) => String(ws.id) !== String(activeWorkspace?.id))
                .map((ws) => (
                <TableRow key={ws.id}>
                  <TableCell className="font-medium text-gray-900">{ws.name}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-gray-500">{ws.slug}</span>
                  </TableCell>
                  <TableCell>
                    <Badge color="gray">{ws.plan}</Badge>
                  </TableCell>
                  <TableCell className="text-gray-500">{ws.monitor_count}</TableCell>
                  <TableCell>
                    {ws.active_incident_count > 0 ? (
                      <Badge color="red">{ws.active_incident_count}</Badge>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500">{ws.member_count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {String(ws.id) !== String(activeWorkspace?.id) && (
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => handleSwitch(ws)}
                        >
                          {t("table.switch")}
                        </Button>
                      )}
                      {canManage && (
                        <Button
                          variant="secondary"
                          size="xs"
                          icon={Users}
                          onClick={() => setManagingWorkspace(ws)}
                        >
                          {t("drawer.title")}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="secondary"
                          size="xs"
                          icon={Trash2}
                          color="red"
                          onClick={() => handleDelete(ws)}
                        >
                          {t("common:common.delete")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {showCreate && (
        <CreateWorkspaceModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["workspaces", "children"] });
            qc.invalidateQueries({ queryKey: ["billing"] });
          }}
        />
      )}

      {managingWorkspace && (
        <ManageMembersDrawer
          workspace={managingWorkspace}
          parentMembers={parentMembers}
          onClose={() => setManagingWorkspace(null)}
        />
      )}
    </div>
  );
}
